import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Copy, Mail, Loader2, X, Check } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

/**
 * Gate 2 · S1 (2026-02) — Invite My Painter dialog.
 * Homeowner sends their finished visualiser project to one specific painter.
 * Two dispatch paths, both fed by the same minted share token:
 *   • Send invite  → server posts an approved-copy email via Resend
 *   • Copy Share Link → clipboard-only; homeowner writes their own message
 *
 * The dialog stays open on error so the user can retry Copy without losing state.
 * Rate-limit and dispatch-failure copy come from the server (429/502 detail).
 */
export default function InviteMyPainterDialog({ open, onOpenChange, projectId }) {
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const [copyingLink, setCopyingLink] = useState(false);
  const [inviteUrl, setInviteUrl] = useState(""); // set after first mint (email or copy)
  const [linkCopied, setLinkCopied] = useState(false);

  const reset = () => {
    setEmail("");
    setNote("");
    setSending(false);
    setCopyingLink(false);
    setInviteUrl("");
    setLinkCopied(false);
  };

  const handleOpen = (next) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const emailIsValid = () => /^\S+@\S+\.\S+$/.test((email || "").trim().toLowerCase());

  const mintInvite = async () => {
    // Server-side idempotency guarantees repeat calls for the same
    // (project, email) return the same token — safe to call from either button.
    const { data } = await api.post(`/visualiser/projects/${projectId}/share`, {
      painter_email: email.trim().toLowerCase(),
      note: note.trim() || null,
    });
    return data;
  };

  const buildInviteUrl = (token) =>
    `${window.location.origin}/shared-invite/${token}`;

  const onSendInvite = async () => {
    if (!emailIsValid()) {
      toast.error("Please enter a valid painter email.");
      return;
    }
    setSending(true);
    try {
      const data = await mintInvite();
      const url = buildInviteUrl(data.token);
      setInviteUrl(url);
      toast.success("Invite sent.");
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      // On 429/502, the server still returns the token in the response body so
      // Copy Share Link continues to work.
      const tokenInBody = err?.response?.data?.token;
      if (tokenInBody) setInviteUrl(buildInviteUrl(tokenInBody));
      if (status === 429) {
        toast.info(detail || "You've reached today's email invite limit. Copy the link and share it directly instead.");
      } else if (status === 502) {
        toast.error(detail || "We couldn't send the email. Copy the link and share it directly instead.");
      } else {
        toast.error(formatApiError(detail) || "Could not send the invite. Please try again.");
      }
    } finally {
      setSending(false);
    }
  };

  const onCopyLink = async () => {
    if (!emailIsValid()) {
      toast.error("Please enter the painter's email first so we can generate the invitation.");
      return;
    }
    setCopyingLink(true);
    try {
      let url = inviteUrl;
      if (!url) {
        // Mint without sending email — same endpoint; if server tries to send
        // and fails, token is still returned via X-Token header and we
        // reconstruct the URL from that.
        try {
          const data = await mintInvite();
          url = buildInviteUrl(data.token);
        } catch (err) {
          const tokenInBody = err?.response?.data?.token;
          if (!tokenInBody) throw err;
          url = buildInviteUrl(tokenInBody);
        }
        setInviteUrl(url);
      }
      await navigator.clipboard.writeText(url);
      setLinkCopied(true);
      toast.success("Invitation link copied.");
      setTimeout(() => setLinkCopied(false), 2500);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not copy the link. Please try again.");
    } finally {
      setCopyingLink(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpen}>
      <DialogContent className="sm:max-w-lg" data-testid="invite-painter-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-brand-ink">Invite my painter</DialogTitle>
          <DialogDescription className="text-sm text-brand-muted">
            Share this project with one specific painter. They&rsquo;ll see your colour choices before they quote.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          <div>
            <label className="text-xs font-semibold text-brand-ink uppercase tracking-wide">Painter&rsquo;s email</label>
            <Input
              type="email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setInviteUrl(""); setLinkCopied(false); }}
              placeholder="painter@example.com"
              data-testid="invite-painter-email"
              className="mt-1.5"
            />
          </div>

          <div className="flex flex-wrap gap-2 pt-2">
            <Button
              type="button"
              onClick={onSendInvite}
              disabled={sending || copyingLink || !emailIsValid()}
              className="bg-brand-navy text-white hover:bg-brand-teal/90 h-11 px-5"
              data-testid="invite-painter-send-btn"
            >
              {sending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</> : <><Mail className="w-4 h-4 mr-2" /> Send invite</>}
            </Button>
            <Button
              type="button"
              onClick={onCopyLink}
              disabled={sending || copyingLink || !emailIsValid()}
              variant="outline"
              className="h-11 px-5 border-brand-navy text-brand-navy hover:bg-brand-sand"
              data-testid="invite-painter-copy-btn"
            >
              {copyingLink ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Preparing…</> : linkCopied ? <><Check className="w-4 h-4 mr-2" /> Copied</> : <><Copy className="w-4 h-4 mr-2" /> Copy share link</>}
            </Button>
          </div>

          {inviteUrl && (
            <div className="text-xs text-brand-muted pt-2 border-t border-brand-border" data-testid="invite-painter-url">
              Invitation URL:&nbsp;
              <code className="bg-brand-sand px-1.5 py-0.5 rounded text-brand-ink text-[11px] break-all">{inviteUrl}</code>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
